<template>
    <VcModal
        :visible="visible"
        :header="t('widget.parameterSetup.modal.title')"
        width="960px"
        custom-class="parameter-wizard-modal"
        @update:visible="onVisibleChange"
        @close="cancel"
    >
        <div class="parameter-wizard flex flex-col overflow-hidden">
            <div
                v-if="showStepper"
                class="parameter-wizard__stepper flex items-stretch w-full mb-6 shrink-0"
            >
                <div
                    v-for="(step, index) in steps"
                    :key="step.parameterName"
                    class="parameter-wizard__step flex flex-1 min-w-0 items-start gap-2 px-2 pb-2"
                    :class="{ 'is-active': index === activeStep }"
                >
                    <RedesignBaseIcon
                        v-if="step.isConfigured && index !== activeStep"
                        name="CheckCircle"
                        :size="24"
                        class="text-primary-actions shrink-0"
                    />
                    <span
                        v-else
                        class="w-6 h-6 rounded-full flex-center text-sm font-semibold shrink-0"
                        :class="index === activeStep
                            ? 'bg-primary text-white'
                            : 'bg-gray-200 text-gray-500'"
                    >
                        {{ index + 1 }}
                    </span>

                    <span class="min-w-0">
                        <span class="flex items-center gap-1.5">
                            <span
                                class="text-sm truncate"
                                :class="index === activeStep || step.isConfigured
                                    ? 'text-default-text font-semibold'
                                    : 'text-gray-400'"
                            >
                                {{ step.label }}
                            </span>
                            <RedesignTag
                                v-if="step.optional"
                                :tag-content="[ { text: String(t('general.optional')) } ]"
                                padding-classes="px-1 py-0"
                            />
                        </span>
                        <span
                            v-if="step.description"
                            class="block text-main-xs text-gray-500 truncate"
                            :title="step.description"
                        >
                            {{ step.description }}
                        </span>
                    </span>
                </div>
            </div>

            <div class="parameter-wizard__content flex flex-col flex-1 min-h-0 overflow-hidden">
                <WidgetParameterSetupSummary
                    v-if="isSummaryStep"
                    :configs="drafts"
                    class="flex-1 min-h-0 overflow-y-auto"
                />
                <component
                    :is="activeEntry.component"
                    v-else-if="activeEntry && activeDraft"
                    :key="activeStepKey"
                    class="flex-1 min-h-0"
                    :value="activeDraft"
                    @update:value="onDraftUpdate"
                />
            </div>
        </div>

        <template #footer>
            <div class="w-full flex items-center justify-between gap-3">
                <div
                    v-if="footerError"
                    class="text-destructive-actions text-sm font-medium"
                >
                    {{ footerError }}
                </div>

                <div class="ml-auto flex items-center gap-3">
                    <VcButton
                        type="outline"
                        color="secondary"
                        @click="cancel"
                    >
                        {{ t('common.cancel') }}
                    </VcButton>

                    <VcButton
                        v-if="activeStep > 0"
                        type="outline"
                        color="secondary"
                        @click="goBack"
                    >
                        {{ t('general.back') }}
                    </VcButton>

                    <VcButton
                        v-if="!isLastStep"
                        :disabled="!isActiveStepValid"
                        @click="goNext"
                    >
                        {{ t('general.next') }}
                    </VcButton>

                    <VcButton
                        v-else
                        :disabled="!isActiveStepValid"
                        @click="save"
                    >
                        {{ t('common.save') }}
                    </VcButton>
                </div>
            </div>
        </template>
    </VcModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import cloneDeep from 'lodash/cloneDeep'
import { VcButton, VcModal } from '@voicenter-team/voicenter-ui-plus'

import RedesignTag from '@/components/RedesignBaseComponents/RedesignTag.vue'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import WidgetParameterSetupSummary
    from '@/components/RedesignComponents/Widgets/Parameters/WidgetParameterSetupSummary.vue'
import { useConfirmBeforeClose } from '@/composables/useConfirmBeforeClose'
import {
    findInvalidWizardParameterIndex,
    getWizardParameterEntry,
    getWizardParameterLabel,
    getWizardParameters,
    isValidWizardParameter
} from '@/components/RedesignComponents/Widgets/widgetParameterRegistry'
import {
    getTemplateParameterHelpDescription
} from '@/components/RedesignComponents/Widgets/widget.helper'
import {
    useWidgetParameterWizardProvide
} from '@/composables/useWidgetParameterWizardProvideInject'
import { WidgetConfig } from '@/types/dashboard'

const SUMMARY_STEP_NAME = '__summary__'

const { t } = useI18n()

const props = defineProps<{
    visible: boolean
    configs: Array<WidgetConfig>
}>()

const emit = defineEmits<{
    (e: 'update:visible', value: boolean): void
    (e: 'save', value: Array<WidgetConfig>): void
}>()

const { closeWithConfirm } = useConfirmBeforeClose()

const drafts = ref<Array<WidgetConfig>>([])
const originalConfigs = ref<Array<WidgetConfig>>([])
const activeStep = ref(0)
const footerError = ref('')

const stepScratch = ref<Record<string, unknown>>({})

useWidgetParameterWizardProvide({
    getParameter: (parameterName: string) => {
        return drafts.value.find(draft => draft.ParameterName === parameterName)
    },
    getOriginalParameter: (parameterName: string) => {
        return originalConfigs.value.find(config => config.ParameterName === parameterName)
    },
    scratch: stepScratch
})

const hasWizard = computed(() => drafts.value.length > 1)

const steps = computed(() => {
    const parameterSteps = drafts.value.map(draft => {
        const entry = getWizardParameterEntry(draft)
        return {
            parameterName: draft.ParameterName ?? '',
            label: getWizardParameterLabel(draft),
            description: String(getTemplateParameterHelpDescription(draft) ?? ''),
            isConfigured: isValidWizardParameter(draft),
            optional: !!entry?.optional
        }
    })

    if (!hasWizard.value) {
        return parameterSteps
    }

    return [
        ...parameterSteps,
        {
            parameterName: SUMMARY_STEP_NAME,
            label: String(t('widget.parameterSetup.summary.step')),
            description: '',
            isConfigured: false,
            optional: false
        }
    ]
})

const showStepper = computed(() => steps.value.length > 1)
const isSummaryStep = computed(() => hasWizard.value && activeStep.value === drafts.value.length)
const isLastStep = computed(() => activeStep.value >= steps.value.length - 1)
const activeDraft = computed(() => drafts.value[activeStep.value])

const isActiveStepValid = computed(() => {
    if (isSummaryStep.value) {
        return findInvalidWizardParameterIndex(drafts.value) === -1
    }
    return !activeDraft.value || isValidWizardParameter(activeDraft.value)
})

const activeEntry = computed(() => {
    return activeDraft.value ? getWizardParameterEntry(activeDraft.value) : undefined
})

const activeStepKey = computed(() => activeDraft.value?.ParameterName ?? '')

function onDraftUpdate (config: WidgetConfig) {
    drafts.value = drafts.value.map((draft, index) => {
        return index === activeStep.value ? config : draft
    })
    footerError.value = ''
}

function validateActiveStep (): boolean {
    const isValid = !activeDraft.value || isValidWizardParameter(activeDraft.value)
    footerError.value = isValid ? '' : String(t('validation.required'))
    return isValid
}

function goBack () {
    footerError.value = ''
    activeStep.value = Math.max(0, activeStep.value - 1)
}

function goNext () {
    if (!validateActiveStep()) {
        return
    }
    activeStep.value = Math.min(steps.value.length - 1, activeStep.value + 1)
}

function save () {
    const invalidIndex = findInvalidWizardParameterIndex(drafts.value)
    if (invalidIndex !== -1) {
        activeStep.value = invalidIndex
        footerError.value = String(t('validation.required'))
        return
    }

    emit('save', cloneDeep(drafts.value))
    emit('update:visible', false)
}

function cancel () {
    closeWithConfirm(() => emit('update:visible', false))
}

function onVisibleChange (value: boolean) {
    if (!value) {
        cancel()
    }
}

watch(
    () => props.visible,
    (open) => {
        if (!open) {
            return
        }

        const wizardParameters = getWizardParameters(props.configs)

        drafts.value = cloneDeep(wizardParameters)
        originalConfigs.value = cloneDeep(wizardParameters)
        activeStep.value = 0
        footerError.value = ''
        stepScratch.value = {}
    },
    { immediate: true }
)
</script>

<style lang="scss" scoped>
.parameter-wizard {
    height: calc(60vh - 32px);

    &__step {
        border-bottom: 2px solid var(--secondary-actions);

        &.is-active {
            border-bottom-color: var(--active-elements);
        }
    }
}
</style>
